import { createFileRoute } from '@tanstack/react-router'
import { pageHead } from '../lib/seo'

const LAST_UPDATED = '2026-05-08'

export const Route = createFileRoute('/privacy')({
  head: () =>
    pageHead({
      path: '/privacy',
      title: 'Privacy Policy',
      description:
        'Privacy Policy for HTML Editor — what data we collect, how we use Google Analytics, and your rights under GDPR.'
    }),
  component: PrivacyPage
})

function PrivacyPage() {
  return (
    <article className="max-w-3xl mx-auto px-4 py-12">
      <h1 className="text-3xl md:text-4xl font-bold mb-2">Privacy Policy</h1>
      <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

      <p className="mb-6 leading-relaxed">
        This Privacy Policy explains what information is collected when you
        use htmleditor.io (the "Service"), how it is used, and the choices you
        have. The Service is operated by{' '}
        <strong>Digital Rocket Factory Oy</strong>, a company registered in
        Finland.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Editor content stays on your device
      </h2>
      <p className="mb-4 leading-relaxed">
        The HTML editor runs entirely in your browser. The content you type,
        paste, or edit is <strong>not transmitted to or stored on our
        servers</strong>. We have no account system and do not associate
        editor content with you.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Analytics — Google Analytics 4
      </h2>
      <p className="mb-4 leading-relaxed">
        We use Google Analytics 4 to understand how visitors use the Service
        in aggregate (page views, approximate location, device and browser
        information, referring sites). This data is processed by Google and
        may include cookies or similar identifiers set by Google in your
        browser. We do not use it to identify individual users.
      </p>
      <p className="mb-4 leading-relaxed">
        You can learn more about Google's practices at{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#ff694a] underline hover:no-underline"
        >
          policies.google.com/privacy
        </a>
        , and you can opt out of Google Analytics across the web with the{' '}
        <a
          href="https://tools.google.com/dlpage/gaoptout"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#ff694a] underline hover:no-underline"
        >
          Google Analytics opt-out browser add-on
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Advertising — Ezoic
      </h2>
      <p className="mb-4 leading-relaxed">
        We may use <strong>Ezoic</strong> and its advertising partners to
        serve ads and measure performance. When this is enabled, Ezoic and its
        partners may set cookies or similar identifiers on your device and
        process information about your visit (such as IP address, device and
        browser data, and pages viewed) to deliver and personalize ads, limit
        ad frequency, and produce analytics.
      </p>
      <p className="mb-4 leading-relaxed">
        Details about Ezoic's data processing and the vendors involved are
        available in Ezoic's privacy policy at{' '}
        <a
          href="https://www.ezoic.com/privacy-policy/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#ff694a] underline hover:no-underline"
        >
          ezoic.com/privacy-policy
        </a>
        .
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Server logs
      </h2>
      <p className="mb-4 leading-relaxed">
        Like most websites, our hosting infrastructure may automatically log
        basic request information (IP address, user agent, timestamp,
        requested URL) for the purposes of operating, securing, and
        troubleshooting the Service. These logs are not used to identify
        individual users.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">Data retention</h2>
      <p className="mb-4 leading-relaxed">
        We retain personal data only as long as needed to operate the
        Service:
      </p>
      <ul className="list-disc pl-6 mb-4 leading-relaxed space-y-1">
        <li>
          <strong>Editor content:</strong> not stored on our servers — it
          stays in your browser session and is discarded when you close the
          page.
        </li>
        <li>
          <strong>Google Analytics data:</strong> retained for the default
          GA4 retention period of <strong>14 months</strong>, after which
          Google automatically deletes user-level and event-level data.
        </li>
        <li>
          <strong>Server request logs:</strong> retained for a short
          operational window (typically up to 30 days) before automatic
          rotation.
        </li>
      </ul>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Security and breach notification
      </h2>
      <p className="mb-4 leading-relaxed">
        We take reasonable technical and organisational measures to protect
        the Service. If a personal data breach occurs that is likely to
        result in a risk to your rights and freedoms, we will notify the
        relevant supervisory authority within 72 hours of becoming aware of
        it, as required by Article 33 of the GDPR, and inform affected
        individuals where the GDPR requires.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Cookies
      </h2>
      <p className="mb-4 leading-relaxed">
        Cookies and similar technologies on the Service are set by the
        third-party services described above (Google Analytics and, when
        active, Ezoic and its partners). You can control or block cookies
        through your browser settings; doing so may not affect the editor
        itself, which works without an account.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Your rights (EU/EEA)
      </h2>
      <p className="mb-4 leading-relaxed">
        If you are in the European Union or European Economic Area, the GDPR
        gives you rights regarding your personal data, including the right to
        access, correct, delete, or restrict processing of your data, and the
        right to object to processing or to data portability. To exercise
        these rights — or to ask any privacy-related question — contact us at{' '}
        <a
          href="mailto:support@htmleditor.io"
          className="text-[#ff694a] underline hover:no-underline"
        >
          support@htmleditor.io
        </a>
        . You also have the right to lodge a complaint with your local data
        protection authority.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">Changes</h2>
      <p className="mb-4 leading-relaxed">
        We may update this Privacy Policy from time to time. The "Last
        updated" date above indicates the latest revision.
      </p>

      <h2 className="text-2xl font-semibold mt-8 mb-3">
        Data controller and contact
      </h2>
      <p className="mb-4 leading-relaxed">
        The data controller for personal data processed via this Service is{' '}
        <strong>Digital Rocket Factory Oy</strong>, a company registered in
        Finland.
        <br />
        Email:{' '}
        <a
          href="mailto:support@htmleditor.io"
          className="text-[#ff694a] underline hover:no-underline"
        >
          support@htmleditor.io
        </a>
      </p>
    </article>
  )
}
